import { Trans, useI18n } from '@i18n';
import { Box, LinearProgress, ListItem, ListItemIcon, ListItemText, Stack } from '@mui/material';
import { async_job_status_type_enum, export_jobs_enum, org_sector_type_enum } from '@predium/enums';
import { translateExportJobsEnum } from '@predium/i18n/client';
import { accessEnum, ensureDefined, maybeEnum } from '@predium/utils';
import { useEffect, useState } from 'react';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import { durationFromNow } from '@/utils/formatTime';

/**
 * How long a batch export may sit at zero progress before we surface the "delayed"
 * notice. Normal BullMQ waiting is sub-second, and the first building's render also
 * takes a few seconds — both keep `total_jobs_finished` at 0 without being "stuck".
 * We only warn once zero progress has persisted well past that, which strongly
 * implies the render queue is genuinely backed up (see the ticket discussion of job
 * status). Overridable for tests.
 */
export const EXPORT_DELAY_WARNING_THRESHOLD_MS = 20_000;

/**
 * The subset of an `async_job` subscription row this row needs. Declared explicitly
 * (rather than the generated subscription type) so it stays decoupled from codegen
 * and trivial to construct in component tests.
 */
export type OngoingExportJob = {
  id: number;
  title: string;
  status_id: async_job_status_type_enum;
  created_at?: string | null;
  total_jobs_created?: number | null;
  total_jobs_finished?: number | null;
};

type Props = {
  job: OngoingExportJob;
  orgSector: org_sector_type_enum;
  /** Override the delay threshold (tests). */
  delayThresholdMs?: number;
};

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

/**
 * A single in-progress export in the downloads popover. For the batch one-pager
 * export it shows a determinate progress bar with a "processed / total" building
 * count, and — only once the job has been stuck at zero progress past the delay
 * threshold — a notice that the export is delayed and will start soon. All other
 * export types keep the indeterminate spinner-style bar.
 */
export default function OngoingExportRow({
  job,
  orgSector,
  delayThresholdMs = EXPORT_DELAY_WARNING_THRESHOLD_MS,
}: Props) {
  const { t, language } = useI18n();

  const jobType = maybeEnum(export_jobs_enum, job.title);
  const isBatchExport = jobType === export_jobs_enum.BATCH_ESG_ANALYSIS_PDF_EXPORT;

  const totalCreated = job.total_jobs_created ?? 0;
  const totalFinished = job.total_jobs_finished ?? 0;
  const progress = totalCreated > 0 ? clampPercent(Math.round((totalFinished / totalCreated) * 100)) : 0;

  // total_jobs_created counts the N item jobs + 1 finalize job; buildings = N.
  const buildingCount = Math.max(0, totalCreated - 1);
  const buildingsProcessed = Math.min(totalFinished, buildingCount);

  // A batch that has finished at least one child job is demonstrably processing.
  const hasStarted = totalFinished > 0;

  // Only warn once zero progress has persisted past the threshold — a timer keyed off
  // created_at so a job that was already stuck when the popover opened warns immediately.
  const [delayElapsed, setDelayElapsed] = useState(false);
  useEffect(() => {
    setDelayElapsed(false);
    if (!isBatchExport || hasStarted) {
      return;
    }
    const createdAtMs = job.created_at ? new Date(job.created_at).getTime() : Date.now();
    const remaining = Math.max(0, delayThresholdMs - (Date.now() - createdAtMs));
    const timer = setTimeout(() => setDelayElapsed(true), remaining);
    return () => clearTimeout(timer);
  }, [isBatchExport, hasStarted, job.created_at, delayThresholdMs]);

  const showDelayed = isBatchExport && !hasStarted && delayElapsed;

  const label = (() => {
    if (!jobType) {
      return job.title;
    }
    if (jobType === export_jobs_enum.ECONOMIC_UNIT_ESG_ANALYSIS_PDF_EXPORT) {
      // Sector-aware label ("Transaction"/"Transaktion" for banking orgs) — the shared
      // enum translator can't apply the orgSector suffix.
      return <Trans i18nKey="Enum_ExportJobsEnum-ECONOMIC_UNIT_ESG_ANALYSIS_PDF_EXPORT" values={{ orgSector }} />;
    }
    return translateExportJobsEnum(accessEnum(export_jobs_enum, job.title));
  })();

  return (
    <ListItem sx={{ py: 1, px: 2.5 }} data-testid="ongoing-export-item">
      <ListItemIcon sx={{ minWidth: 48 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: 'background.neutral',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify
            icon={isBatchExport ? ICONS.ZIP : ICONS.EVA_FILE_TEXT_OUTLINE}
            width={24}
            height={24}
            data-testid="export-icon"
          />
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={
          <PTypography variant="subtitle2" sx={{ mb: 0.5, whiteSpace: 'nowrap' }}>
            {label}
          </PTypography>
        }
        secondary={
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LinearProgress
                variant={isBatchExport ? 'determinate' : 'indeterminate'}
                value={isBatchExport ? progress : undefined}
                sx={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                }}
                data-testid="progress-bar"
              />
              {isBatchExport && buildingCount > 0 ? (
                <Box component="span" data-testid="progress-count" sx={{ whiteSpace: 'nowrap' }}>
                  <PTypography variant="caption" color="secondary">
                    {buildingsProcessed} / {buildingCount}
                  </PTypography>
                </Box>
              ) : null}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
              <Iconify icon={ICONS.CLOCK} width={16} height={16} />
              {showDelayed ? (
                <Box component="span" data-testid="export-delayed-warning">
                  <PTypography variant="caption" color="secondary">
                    {t('Downloads_ExportDelayed')}
                  </PTypography>
                </Box>
              ) : (
                <PTypography variant="caption" color="secondary">
                  {durationFromNow(ensureDefined(job.created_at), language)}
                </PTypography>
              )}
            </Stack>
          </Box>
        }
      />
    </ListItem>
  );
}
